/**
 * Open the system browser to the Bridges connect page for CLI linking.
 */

import open from "open";
import { log, type LogFn } from "../log.js";

function isLocalApiUrl(apiUrl: string): boolean {
  try {
    const u = new URL(apiUrl);
    const host = u.hostname.toLowerCase();
    return host === "localhost" || host === "127.0.0.1";
  } catch {
    return false;
  }
}

export function appUrlForApiUrl(apiUrl?: string): string {
  return apiUrl && isLocalApiUrl(apiUrl)
    ? (process.env.BUILDAUTOMATON_APP_URL ?? "http://localhost:3000")
    : (process.env.BUILDAUTOMATON_APP_URL ?? "https://app.buildautomaton.com");
}

/**
 * Open the default browser to the connect URL so the user can sign in and link this CLI.
 * When apiUrl is local (localhost/127.0.0.1), opens the local app so the approve request
 * hits the same backend the CLI is connected to.
 * @param connectionId - Pending connection ID from the backend
 * @param initialWorkspaceId - Optional workspace ID to pre-fill in the URL
 * @param preferredBridgeName - Optional bridge name (alphanumeric and underscores only)
 * @param apiUrl - Optional API URL the CLI is using; if local, opens local app
 * @param logFn - Optional callback for log messages (default: shared log)
 */
export async function openBrowser(
  connectionId: string,
  initialWorkspaceId?: string,
  preferredBridgeName?: string,
  apiUrl?: string,
  logFn: LogFn = log
): Promise<void> {
  const appUrl = appUrlForApiUrl(apiUrl);
  let connectCliUrl = `${appUrl.replace(/\/$/, "")}/bridges/connect?connectionId=${connectionId}`;
  if (initialWorkspaceId) {
    try {
      const workspaceB64 = Buffer.from(initialWorkspaceId, "utf8").toString("base64url");
      connectCliUrl += `&workspace=${workspaceB64}`;
    } catch (_) {
      // ignore
    }
  }
  if (preferredBridgeName?.trim()) {
    connectCliUrl += `&bridgeName=${encodeURIComponent(preferredBridgeName.trim())}`;
  }
  logFn("Opening browser to link this CLI…");
  logFn(
    "If you’re already signed in with one workspace, the CLI will connect automatically. Otherwise sign in and link to a workspace."
  );
  try {
    await open(connectCliUrl, { wait: false });
  } catch {
    logFn("Could not open browser. Open this URL manually:");
    logFn(connectCliUrl);
  }
}
